import { useCallback, useEffect, useRef } from 'react';

/*
  useAutosave (v2)
  - Debounced scheduling of save operations with a default delay
  - Flushes on pagehide/visibilitychange (hidden) to reduce data loss risk
  - Retries are left to the caller; this hook focuses on timing concerns
*/

export default function useAutosave({ delayMs = 400 } = {}) {
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const schedule = useCallback((fn) => {
    pendingRef.current = fn;
    clearTimer();
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      const toRun = pendingRef.current;
      pendingRef.current = null;
      if (toRun) {
        try { await toRun(); } catch (_) { /* caller handles */ }
      }
    }, delayMs);
  }, [delayMs]);

  const flush = useCallback(async () => {
    clearTimer();
    const toRun = pendingRef.current;
    pendingRef.current = null;
    if (toRun) {
      try { await toRun(); } catch (_) { /* caller handles */ }
    }
  }, []);

  useEffect(() => {
    const handlePagehide = () => { void flush(); };
    const handleVisibility = () => { if (document.visibilityState === 'hidden') void flush(); };
    window.addEventListener('pagehide', handlePagehide);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pagehide', handlePagehide);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimer();
    };
  }, [flush]);

  return { schedule, flush };
}


